import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

/**
 * The long-form review.
 *
 * ## Why the page needed one
 *
 * The comparison pages carried a verdict, a decision rule and a table, and
 * stopped. That is enough to make a shortlist and nowhere near enough to defend
 * one: a reader who has to argue for this internally needs to know what the
 * other product actually IS, why the two are built the way they are, and what
 * it would cost to be wrong. A table cell cannot hold any of that — it can hold
 * a claim, which is a thing to be checked rather than a thing that has been.
 *
 * ## Seven sections, and the order is the argument
 *
 * `what` → `built` → `themBetter` → `usBetter` → `cost` → `switching` →
 * `bottomLine`. The rival's case is put BEFORE ours, deliberately: a comparison
 * that reaches its own product first has told the reader what kind of document
 * it is by the second paragraph, and everything after that is discounted.
 * Putting the honest case for the alternative first is what buys the rest of
 * the page a hearing — and on several of these rivals it is genuinely the right
 * answer, which is why the section can be written straight.
 *
 * ## The two "better choice" sections are rendered as ONE block
 *
 * `themBetter` and `usBetter` are the head-to-head, and they shipped as two
 * consecutive grey paragraphs indistinguishable from the five around them — the
 * comparison's central argument, laid out as though it were background. They
 * now render side by side on a wide viewport, the rival's case plain and ours
 * on the tint with the brand rule, so the contrast the words are making is
 * visible before a word is read. They stack in source order on a phone, which
 * keeps the rival-first ordering intact where there is no "side by side".
 *
 * ## OPEN by default, and that is the whole point
 *
 * This shipped collapsed, on the reasoning that the concise answer stays at the
 * top and nobody is made to read 2,000 words. It was the wrong call, and the
 * way it was wrong is worth keeping written down, because it looked careful.
 *
 * A collapsed disclosure does not read as "depth is available". It reads as
 * "there is nothing here" — the page renders as the same four-paragraph stub it
 * was before the review existed, and the reader's verdict is formed before they
 * ever consider clicking. Measured on `/vs/sierra`: 7,319 characters in the
 * DOM, 1,733 of them visible. The research was done, shipped, and invisible.
 *
 * So it opens. The summary stays a real `<summary>` — a reader who wants the
 * verdict and nothing else can still collapse it, and that is the direction the
 * affordance should run: opt OUT of the detail, never opt in.
 *
 * ## `<details>` rather than a state hook
 *
 * Still a `<details>`, because it is open-able before hydration, printable,
 * findable by the browser's own find-in-page (Chromium and Firefox expand a
 * closed `<details>` to reveal a match), and indexable. A `useState` disclosure
 * is none of those, and this is the one surface on the site where being read by
 * a crawler is the point.
 *
 * The summary carries `list-none` plus a rotating chevron because the UA
 * triangle is unstyleable and sits outside the padding; `[&::-webkit-details-marker]`
 * is Safari's own name for the same thing.
 */

/** The five sections that read as one column, in render order. */
const SOLO_BEFORE = ['what', 'built'] as const;
const SOLO_AFTER = ['cost', 'switching', 'bottomLine'] as const;

/** Ordinals are FIXED to the argument's order, not to render order — the
 *  head-to-head pair is 03/04 wherever it lands in the layout. */
const ORDINAL: Record<string, string> = {
  what: '01',
  built: '02',
  themBetter: '03',
  usBetter: '04',
  cost: '05',
  switching: '06',
  bottomLine: '07',
};

function Marker({ n }: { readonly n: string }): ReactElement {
  return (
    <span
      aria-hidden="true"
      dir="ltr"
      className="grid size-7 flex-none place-items-center rounded-full border border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] font-mono text-[0.7rem] tabular-nums text-[color:var(--bd-subtle)]"
    >
      {n}
    </span>
  );
}

function Section({
  slug,
  section,
  rivalName,
}: {
  readonly slug: string;
  readonly section: string;
  readonly rivalName: string;
}): ReactElement {
  const { t } = useT();
  return (
    <section aria-labelledby={`review-${slug}-${section}`}>
      <h3
        id={`review-${slug}-${section}`}
        className="flex items-center gap-3 text-[1.08rem] font-semibold leading-[1.3] tracking-[-0.02em] text-[color:var(--bd-ink)]"
      >
        <Marker n={ORDINAL[section] ?? ''} />
        {t(`vs.review.section.${section}`, { rival: rivalName })}
      </h3>
      {/* `max-w-[74ch]`: the review is the only long-form prose on the site, and
          the page runs at the `wide` container so the table can breathe.
          Unbounded, a paragraph here would set at ~120 characters — past the
          point where the eye reliably finds the start of the next line. */}
      <p className="ms-10 mt-3 max-w-[74ch] whitespace-pre-line text-[1rem] leading-[1.75] text-[color:var(--bd-body)]">
        {t(`vs.review.${slug}.${section}`)}
      </p>
    </section>
  );
}

export function FullReview({
  slug,
  rivalName,
}: {
  /** Camel-cased rival id — the `vs.review.<id>.*` namespace. */
  readonly slug: string;
  readonly rivalName: string;
}): ReactElement {
  const { t } = useT();

  return (
    <details
      open
      className="group overflow-hidden rounded-[var(--bd-radius)] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)]"
      data-syncanix-full-review
    >
      <summary className="flex cursor-pointer list-none items-center gap-4 px-6 py-5 transition-colors hover:bg-[color:var(--bd-sunk)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--bd-blue)] [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1">
          <span
            id="full-review-heading"
            className="block text-[1.15rem] font-semibold leading-[1.25] tracking-[-0.022em] text-[color:var(--bd-ink)]"
          >
            {t('vs.review.heading', { rival: rivalName })}
          </span>
          <span className="mt-1 block text-[0.92rem] leading-[1.55] text-[color:var(--bd-muted)]">
            {t('vs.review.sub')}
          </span>
        </span>
        <span
          aria-hidden="true"
          className="grid size-8 flex-none place-items-center rounded-full border border-[color:var(--bd-line)] text-[color:var(--bd-muted)] transition-transform duration-300 group-open:rotate-180"
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none">
            <path
              d="M2.5 4.5 6 8l3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </summary>

      <div className="border-t border-[color:var(--bd-line)] px-6 py-8 sm:px-9 sm:py-10">
        <div className="flex flex-col gap-10">
          {SOLO_BEFORE.map((section) => (
            <Section key={section} slug={slug} section={section} rivalName={rivalName} />
          ))}

          {/* The head-to-head. Two grounds, one rule down the middle on a wide
              viewport — the argument made visible before it is read. */}
          <div className="grid grid-cols-1 items-stretch gap-px overflow-hidden rounded-[14px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-line-2)] lg:grid-cols-2">
            <div className="bg-[color:var(--bd-page)] p-6 sm:p-7">
              <h3
                id={`review-${slug}-themBetter`}
                className="flex items-center gap-3 text-[1.08rem] font-semibold leading-[1.3] tracking-[-0.02em] text-[color:var(--bd-ink)]"
              >
                <Marker n={ORDINAL.themBetter ?? ''} />
                {t('vs.review.section.themBetter', { rival: rivalName })}
              </h3>
              <p className="mt-3 whitespace-pre-line text-[1rem] leading-[1.75] text-[color:var(--bd-muted)]">
                {t(`vs.review.${slug}.themBetter`)}
              </p>
            </div>
            <div className="relative bg-[color:var(--bd-tint)] p-6 sm:p-7">
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-[3px]"
                style={{ background: 'var(--bd-gradient)' }}
              />
              <h3
                id={`review-${slug}-usBetter`}
                className="flex items-center gap-3 text-[1.08rem] font-semibold leading-[1.3] tracking-[-0.02em] text-[color:var(--bd-ink)]"
              >
                <Marker n={ORDINAL.usBetter ?? ''} />
                {t('vs.review.section.usBetter', { rival: rivalName })}
              </h3>
              <p className="mt-3 whitespace-pre-line text-[1rem] leading-[1.75] text-[color:var(--bd-body)]">
                {t(`vs.review.${slug}.usBetter`)}
              </p>
            </div>
          </div>

          {SOLO_AFTER.map((section) => (
            <Section key={section} slug={slug} section={section} rivalName={rivalName} />
          ))}
        </div>

        <p className="ms-10 mt-10 max-w-[74ch] border-t border-[color:var(--bd-line)] pt-5 text-[0.88rem] leading-[1.6] text-[color:var(--bd-subtle)]">
          {t('vs.review.disclosure', { rival: rivalName })}
        </p>
      </div>
    </details>
  );
}
